import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import {
  type FC,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const DEFAULT_PANEL_HEIGHT = 400;
const MIN_PANEL_HEIGHT = 150;
const MAX_PANEL_HEIGHT_RATIO = 0.9; // 90% of viewport height
const PANEL_HEIGHT_STORAGE_KEY = 'devtools-query-panel-height';

const getStoredPanelHeight = (): number => {
  const stored = localStorage.getItem(PANEL_HEIGHT_STORAGE_KEY);
  if (stored !== null) {
    const parsed = Number.parseInt(stored, 10);
    if (!Number.isNaN(parsed) && parsed >= MIN_PANEL_HEIGHT) {
      return parsed;
    }
  }
  return DEFAULT_PANEL_HEIGHT;
};

const storePanelHeight = (height: number): void => {
  localStorage.setItem(PANEL_HEIGHT_STORAGE_KEY, String(height));
};

type ResizableQueryDevtoolsPanelProps = {
  onClose: () => void;
};

/**
 * A resizable wrapper around ReactQueryDevtoolsPanel.
 * - Adds a drag handle at the top for vertical resizing
 * - Persists panel height to localStorage
 * - Adds body padding so page content remains scrollable
 */
export const ResizableQueryDevtoolsPanel: FC<
  ResizableQueryDevtoolsPanelProps
> = ({ onClose }) => {
  const [panelHeight, setPanelHeight] = useState(getStoredPanelHeight);
  const isResizingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  // Add padding to body so content remains scrollable above the panel
  useEffect(() => {
    document.body.style.paddingBottom = `${panelHeight}px`;

    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [panelHeight]);

  const handleResizeMouseDown = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault();
      isResizingRef.current = true;
      startYRef.current = event.clientY;
      startHeightRef.current = panelHeight;
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    },
    [panelHeight],
  );

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizingRef.current) {
        return;
      }

      const deltaY = startYRef.current - event.clientY;
      const maxHeight = window.innerHeight * MAX_PANEL_HEIGHT_RATIO;
      const newHeight = Math.min(
        maxHeight,
        Math.max(MIN_PANEL_HEIGHT, startHeightRef.current + deltaY),
      );

      setPanelHeight(newHeight);
    };

    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Persist the final height
        storePanelHeight(panelHeight);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [panelHeight]);

  return (
    <div
      className='fixed bottom-0 left-0 right-0 z-[9998] flex flex-col'
      style={{ height: panelHeight }}
    >
      {/* Resize handle */}
      <div
        onMouseDown={handleResizeMouseDown}
        className='h-2 bg-gray-300 dark:bg-gray-600 cursor-ns-resize flex items-center justify-center hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors shrink-0'
      >
        <div className='w-12 h-1 bg-gray-500 dark:bg-gray-400 rounded-full' />
      </div>
      <div className='flex-1 overflow-hidden'>
        <ReactQueryDevtoolsPanel
          onClose={onClose}
          style={{ height: '100%', width: '100%' }}
        />
      </div>
    </div>
  );
};
